/* ============================================================
   TheTransformationLab — WebGL particle body-morph engine
   No dependencies. ~12kb. Renders a point-cloud human figure
   that morphs from a "before" build to an "after" build,
   driven by scroll, with mouse parallax and depth.
   ============================================================ */
(function () {
  'use strict';

  var canvas = document.getElementById('hero-gl');
  if (!canvas) return;

  var gl = canvas.getContext('webgl', {
    antialias: false,
    alpha: true,
    premultipliedAlpha: false,
    powerPreference: 'high-performance'
  });
  if (!gl) { document.documentElement.classList.add('no-gl'); return; }

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isMobile = window.matchMedia('(max-width: 860px)').matches;
  var COUNT = isMobile ? 15000 : 26000;

  /* ---------- tiny mat4 ---------- */
  function perspective(fov, aspect, near, far) {
    var f = 1 / Math.tan(fov / 2), nf = 1 / (near - far);
    return new Float32Array([
      f / aspect, 0, 0, 0,
      0, f, 0, 0,
      0, 0, (far + near) * nf, -1,
      0, 0, 2 * far * near * nf, 0
    ]);
  }
  function mul(a, b) {
    var o = new Float32Array(16);
    for (var i = 0; i < 4; i++) for (var j = 0; j < 4; j++) {
      var s = 0;
      for (var k = 0; k < 4; k++) s += a[k * 4 + j] * b[i * 4 + k];
      o[i * 4 + j] = s;
    }
    return o;
  }
  function rotY(a) {
    var c = Math.cos(a), s = Math.sin(a);
    return new Float32Array([c, 0, -s, 0, 0, 1, 0, 0, s, 0, c, 0, 0, 0, 0, 1]);
  }
  function rotX(a) {
    var c = Math.cos(a), s = Math.sin(a);
    return new Float32Array([1, 0, 0, 0, 0, c, s, 0, 0, -s, c, 0, 0, 0, 0, 1]);
  }
  function translate(x, y, z) {
    return new Float32Array([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, x, y, z, 1]);
  }

  /* ---------- deterministic rng so both builds correspond ---------- */
  function mulberry(seed) {
    return function () {
      seed |= 0; seed = seed + 0x6D2B79F5 | 0;
      var t = Math.imul(seed ^ seed >>> 15, 1 | seed);
      t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
  }

  /* ---------- body model ----------
     Parametric so point i maps to the same anatomical spot in
     both silhouettes — that is what makes the morph read as a
     body changing shape rather than noise re-shuffling.        */

  var BEFORE = {
    shoulder: 0.325, chest: 0.350, waist: 0.340, hip: 0.320,
    arm: 0.062, leg: 0.105, headR: 0.058, depth: 1.15, slouch: 0.020
  };
  var AFTER = {
    shoulder: 0.385, chest: 0.345, waist: 0.205, hip: 0.255,
    arm: 0.056, leg: 0.098, headR: 0.055, depth: 0.80, slouch: 0.0
  };

  // vertical anchors (0 = top of head, 1 = feet)
  var Y_HEAD = 0.075, Y_NECK = 0.155, Y_SHOULDER = 0.205,
      Y_CHEST = 0.305, Y_WAIST = 0.445, Y_HIP = 0.560,
      Y_WRIST = 0.585, Y_FOOT = 0.965;

  function smooth(t) { return t * t * (3 - 2 * t); }

  // torso half-width at normalised height u (0 shoulder -> 1 hip)
  function torsoHalf(p, u) {
    var y = Y_SHOULDER + (Y_HIP - Y_SHOULDER) * u, w;
    if (y < Y_CHEST) {
      w = p.shoulder + (p.chest - p.shoulder) * smooth((y - Y_SHOULDER) / (Y_CHEST - Y_SHOULDER));
    } else if (y < Y_WAIST) {
      w = p.chest + (p.waist - p.chest) * smooth((y - Y_CHEST) / (Y_WAIST - Y_CHEST));
    } else {
      w = p.waist + (p.hip - p.waist) * smooth((y - Y_WAIST) / (Y_HIP - Y_WAIST));
    }
    return w * 0.5;
  }

  /* build one silhouette's positions into a Float32Array(count*3) */
  function buildBody(p, count) {
    var out = new Float32Array(count * 3);
    var rnd = mulberry(1337); // same seed => same i -> same anatomy
    for (var i = 0; i < count; i++) {
      var pick = rnd(), a = rnd(), b = rnd(), c = rnd(), d = rnd();
      var x = 0, y = 0, z = 0, thick = 0;

      if (pick < 0.075) {
        /* head — sampled on a slightly flattened sphere shell */
        var th = a * Math.PI * 2, ph = Math.acos(2 * b - 1);
        var r = p.headR * (0.72 + 0.28 * Math.cbrt(c));
        x = r * Math.sin(ph) * Math.cos(th);
        y = Y_HEAD + r * Math.cos(ph) * 1.18;
        z = r * Math.sin(ph) * Math.sin(th) * 0.92;
      } else if (pick < 0.115) {
        /* neck */
        var u1 = a;
        y = Y_NECK - (Y_NECK - Y_HEAD - p.headR * 0.6) * (1 - u1) * 0.5;
        y = Y_HEAD + p.headR * 0.9 + (Y_SHOULDER - Y_HEAD - p.headR * 0.9) * u1;
        var nr = 0.030 * (0.6 + 0.4 * (1 - u1));
        var na = b * Math.PI * 2, nrr = nr * Math.sqrt(c);
        x = Math.cos(na) * nrr; z = Math.sin(na) * nrr * p.depth;
      } else if (pick < 0.525) {
        /* torso */
        var u = a;
        var hw = torsoHalf(p, u);
        y = Y_SHOULDER + (Y_HIP - Y_SHOULDER) * u;
        // elliptical cross-section, denser near the surface
        var ang = b * Math.PI * 2;
        var rad = Math.pow(c, 0.42);
        x = Math.cos(ang) * rad * hw;
        z = Math.sin(ang) * rad * hw * 0.62 * p.depth;
        y += p.slouch * smooth(u) * 0.6;
      } else if (pick < 0.735) {
        /* arms */
        var side = d < 0.5 ? -1 : 1;
        var t = a;
        var sx = side * (torsoHalf(p, 0.02) + p.arm * 0.55);
        var ex = side * (torsoHalf(p, 1) + p.arm * 1.55);
        var cx2 = sx + (ex - sx) * t;
        var taper = 1 - 0.34 * t;
        var ar = p.arm * 0.5 * taper;
        var aa = b * Math.PI * 2, arr = ar * Math.pow(c, 0.42);
        x = cx2 + Math.cos(aa) * arr;
        y = Y_SHOULDER + 0.012 + (Y_WRIST - Y_SHOULDER) * t;
        z = Math.sin(aa) * arr * p.depth;
      } else {
        /* legs */
        var lside = d < 0.5 ? -1 : 1;
        var lt = a;
        var hx = lside * (torsoHalf(p, 1) * 0.48 + p.leg * 0.1);
        var fx = lside * 0.052;
        var lcx = hx + (fx - hx) * smooth(lt);
        var ltaper = 1 - 0.40 * smooth(lt);
        var lr = p.leg * 0.5 * ltaper;
        var la = b * Math.PI * 2, lrr = lr * Math.pow(c, 0.42);
        x = lcx + Math.cos(la) * lrr;
        y = Y_HIP - 0.02 + (Y_FOOT - Y_HIP + 0.02) * lt;
        z = Math.sin(la) * lrr * p.depth;
      }

      // centre the figure, flip to GL's +Y up, scale to view units
      out[i * 3] = x * 3.0;
      out[i * 3 + 1] = (0.52 - y) * 3.0;
      out[i * 3 + 2] = z * 3.0;
    }
    return out;
  }

  var posA = buildBody(BEFORE, COUNT);
  var posB = buildBody(AFTER, COUNT);

  // per-particle randoms: drift seed, size, colour offset, dissolve order
  var seeds = new Float32Array(COUNT * 4);
  var rs = mulberry(9001);
  for (var i = 0; i < COUNT; i++) {
    seeds[i * 4] = rs() * 6.283;
    seeds[i * 4 + 1] = 0.55 + rs() * 0.85;
    seeds[i * 4 + 2] = rs();
    seeds[i * 4 + 3] = rs();
  }

  /* ---------- shaders ---------- */
  var VS = [
    'attribute vec3 aA; attribute vec3 aB; attribute vec4 aS;',
    'uniform mat4 uProj, uView;',
    'uniform float uMorph, uTime, uSize, uBurst, uDpr;',
    'varying float vT; varying float vFade;',
    'void main(){',
    '  float m = clamp(uMorph, 0.0, 1.0);',
    // stagger each particle's transition so the change sweeps up the body
    '  float lead = clamp((m - aS.z * 0.34) / 0.66, 0.0, 1.0);',
    '  lead = lead*lead*(3.0-2.0*lead);',
    '  vec3 p = mix(aA, aB, lead);',
    // organic breathing drift
    '  float w = uTime * 0.55 + aS.x;',
    '  p += vec3(sin(w)*0.030, cos(w*0.83)*0.030, sin(w*0.62)*0.030);',
    // scroll-out burst: particles scatter outward and up
    '  float bz = uBurst * (0.4 + aS.w);',
    '  p += normalize(p + vec3(0.001)) * bz * 2.6;',
    '  p.y += bz * 1.2;',
    '  vec4 mv = uView * vec4(p, 1.0);',
    '  gl_Position = uProj * mv;',
    '  float att = 260.0 / max(-mv.z, 0.1);',
    '  gl_PointSize = uSize * aS.y * att * uDpr * (1.0 - uBurst*0.45);',
    '  vT = lead;',
    '  vFade = clamp(1.0 - uBurst*1.15, 0.0, 1.0) * clamp((-mv.z - 1.0)*0.35, 0.15, 1.0);',
    '}'
  ].join('\n');

  var FS = [
    'precision mediump float;',
    'varying float vT; varying float vFade;',
    'uniform vec3 uColA, uColB;',
    'void main(){',
    '  vec2 d = gl_PointCoord - 0.5;',
    '  float r = dot(d, d);',
    '  if (r > 0.25) discard;',
    '  float a = smoothstep(0.25, 0.0, r);',
    '  vec3 col = mix(uColA, uColB, vT);',
    '  gl_FragColor = vec4(col, a * 0.85 * vFade);',
    '}'
  ].join('\n');

  function compile(type, src) {
    var s = gl.createShader(type);
    gl.shaderSource(s, src); gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      console.warn(gl.getShaderInfoLog(s)); return null;
    }
    return s;
  }
  var vs = compile(gl.VERTEX_SHADER, VS), fs = compile(gl.FRAGMENT_SHADER, FS);
  if (!vs || !fs) { document.documentElement.classList.add('no-gl'); return; }
  var prog = gl.createProgram();
  gl.attachShader(prog, vs); gl.attachShader(prog, fs); gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    document.documentElement.classList.add('no-gl'); return;
  }
  gl.useProgram(prog);

  function buf(data, loc, size) {
    var b = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, b);
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
    var l = gl.getAttribLocation(prog, loc);
    gl.enableVertexAttribArray(l);
    gl.vertexAttribPointer(l, size, gl.FLOAT, false, 0, 0);
  }
  buf(posA, 'aA', 3); buf(posB, 'aB', 3); buf(seeds, 'aS', 4);

  var U = {};
  ['uProj', 'uView', 'uMorph', 'uTime', 'uSize', 'uBurst', 'uDpr', 'uColA', 'uColB']
    .forEach(function (n) { U[n] = gl.getUniformLocation(prog, n); });

  gl.uniform3f(U.uColA, 0.40, 0.51, 0.74);   // steel blue — "before"
  gl.uniform3f(U.uColB, 0.83, 1.00, 0.31);   // lime — "after"
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
  gl.disable(gl.DEPTH_TEST);

  /* ---------- sizing ---------- */
  var dpr = 1, W = 0, H = 0, proj;
  function resize() {
    dpr = Math.min(window.devicePixelRatio || 1, isMobile ? 2 : 1.75);
    var r = canvas.getBoundingClientRect();
    W = Math.max(1, Math.round(r.width * dpr));
    H = Math.max(1, Math.round(r.height * dpr));
    canvas.width = W; canvas.height = H;
    gl.viewport(0, 0, W, H);
    proj = perspective(0.62, r.width / Math.max(r.height, 1), 0.1, 100);
    gl.uniformMatrix4fv(U.uProj, false, proj);
    gl.uniform1f(U.uDpr, dpr);
  }
  resize();
  var rt;
  window.addEventListener('resize', function () {
    clearTimeout(rt); rt = setTimeout(function () {
      isMobile = window.matchMedia('(max-width: 860px)').matches; resize();
    }, 150);
  });

  /* ---------- interaction state ---------- */
  var mx = 0, my = 0, tmx = 0, tmy = 0;
  if (!isMobile) {
    window.addEventListener('pointermove', function (e) {
      tmx = (e.clientX / window.innerWidth - 0.5) * 2;
      tmy = (e.clientY / window.innerHeight - 0.5) * 2;
    }, { passive: true });
  }

  var morph = 0, tmorph = 0, burst = 0, tburst = 0;
  var section = canvas.closest('.hero') || canvas.parentElement;
  var meterFill = document.querySelector('.hero__meter i');
  var meterLabel = document.querySelector('.hero__meter b');

  function readScroll() {
    var r = section.getBoundingClientRect();
    var vh = window.innerHeight;
    // progress through the pinned hero region
    var p = (-r.top) / Math.max(r.height - vh, 1);
    p = Math.min(Math.max(p, 0), 1);
    tmorph = Math.min(p / 0.72, 1);
    tburst = Math.max(0, (p - 0.80) / 0.20);
  }
  readScroll();
  window.addEventListener('scroll', readScroll, { passive: true });

  /* ---------- loop ---------- */
  var start = performance.now(), running = true, last = 0;
  var io = new IntersectionObserver(function (es) {
    running = es[0].isIntersecting;
    if (running) requestAnimationFrame(frame);
  }, { threshold: 0 });
  io.observe(canvas);

  function frame(now) {
    if (!running) return;
    if (now - last < 1000 / 61) { requestAnimationFrame(frame); return; }
    last = now;
    var t = (now - start) / 1000;

    mx += (tmx - mx) * 0.055;
    my += (tmy - my) * 0.055;
    morph += (tmorph - morph) * 0.075;
    burst += (tburst - burst) * 0.10;

    var idle = reduce ? 0 : Math.sin(t * 0.22) * 0.16;
    var tx = isMobile ? 0 : 0.92;
    var tz = isMobile ? -8.6 : -5.55;
    var view = mul(translate(tx, isMobile ? 0.78 : -0.10, tz),
                   mul(rotX(my * 0.14 - 0.02), rotY(mx * 0.42 + idle)));

    gl.uniformMatrix4fv(U.uView, false, view);
    gl.uniform1f(U.uMorph, morph);
    gl.uniform1f(U.uTime, reduce ? 0 : t);
    gl.uniform1f(U.uBurst, burst);
    gl.uniform1f(U.uSize, isMobile ? 0.034 : 0.021);

    if (meterFill) {
      meterFill.style.setProperty('--p', morph.toFixed(3));
      var lbl = morph < 0.12 ? 'Day one' : morph < 0.55 ? 'In progress' : morph < 0.9 ? 'Building' : 'Transformed';
      if (meterLabel && meterLabel.textContent !== lbl) meterLabel.textContent = lbl;
    }

    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.POINTS, 0, COUNT);

    requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);
  document.documentElement.classList.add('has-gl');
})();
